type PathSegment = string | number;

function toSegments(path: string): PathSegment[] {
  return path
    .split(".")
    .filter(Boolean)
    .map((part) => {
      const asNumber = Number(part);
      return Number.isInteger(asNumber) && String(asNumber) === part ? asNumber : part;
    });
}

export function getValueAtPath<T = unknown>(source: unknown, path: string): T | undefined {
  if (!path) return source as T;
  const segments = toSegments(path);
  let current: unknown = source;

  for (const segment of segments) {
    if (current == null) return undefined;
    if (typeof segment === "number" && Array.isArray(current)) {
      current = current[segment];
      continue;
    }
    if (typeof segment === "string" && typeof current === "object") {
      current = (current as Record<string, unknown>)[segment];
      continue;
    }
    return undefined;
  }
  return current as T;
}

export function setValueAtPath<T extends object>(source: T, path: string, value: unknown): T {
  if (!path) return value as T;
  const segments = toSegments(path);
  const draft: any = Array.isArray(source) ? [...(source as any)] : { ...(source as any) };
  let cursor: any = draft;

  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    const nextSegment = segments[i + 1];
    const existing = cursor[segment as any];

    if (Array.isArray(existing)) {
      cursor[segment as any] = [...existing];
    } else if (existing && typeof existing === "object") {
      cursor[segment as any] = { ...existing };
    } else {
      cursor[segment as any] = typeof nextSegment === "number" ? [] : {};
    }
    cursor = cursor[segment as any];
  }

  cursor[segments[segments.length - 1] as any] = value;
  return draft as T;
}

export function removeArrayItemAtPath<T extends object>(source: T, path: string, index: number): T {
  const current = getValueAtPath<unknown[]>(source, path);
  if (!Array.isArray(current)) return source;
  const next = current.filter((_, i) => i !== index);
  return setValueAtPath(source, path, next);
}

export function addArrayItemAtPath<T extends object>(source: T, path: string, value: unknown): T {
  const current = getValueAtPath<unknown[]>(source, path);
  const next = Array.isArray(current) ? [...current, value] : [value];
  return setValueAtPath(source, path, next);
}
